import { graphql, useQuery } from '@/lib/graphql';
import type { ReactNode } from 'react';

const GET_TRACKING_SUBSCRIPTION_QUERY = graphql(`
  query TrackingSubscriptionStatus {
    trackingTargets(page: 1, perPage: 1) {
      pageInfo {
        totalCount
      }
    }
  }
`);

type RetryableFallback =
  | ReactNode
  | ((props: { retry: () => void }) => ReactNode);

type TrackingFeatureGateProps = {
  children: ReactNode;
  loadingFallback?: ReactNode;
  deniedFallback?: ReactNode;
  errorFallback?: RetryableFallback;
};

const useTrackingFeatureVisibility = () => {
  const { data, loading, error, refetch } = useQuery(
    GET_TRACKING_SUBSCRIPTION_QUERY
  );
  const totalCount = data?.trackingTargets.pageInfo.totalCount;

  if (typeof totalCount === 'number') {
    return {
      visible: totalCount > 0,
      isLoading: false,
      hasError: false,
      retry: () => {
        void refetch();
      },
    };
  }

  if (loading && !error) {
    return {
      visible: false,
      isLoading: true,
      hasError: false,
      retry: () => {
        void refetch();
      },
    };
  }

  return {
    visible: false,
    isLoading: false,
    hasError: true,
    retry: () => {
      void refetch();
    },
  };
};

const renderFallback = (
  fallback: RetryableFallback | undefined,
  retry: () => void
) => {
  if (typeof fallback === 'function') {
    return fallback({ retry });
  }

  return fallback ?? null;
};

export const TrackingFeatureGate = ({
  children,
  loadingFallback = null,
  deniedFallback = null,
  errorFallback = null,
}: TrackingFeatureGateProps) => {
  const { visible, isLoading, hasError, retry } =
    useTrackingFeatureVisibility();

  if (isLoading) return loadingFallback;
  if (hasError) return renderFallback(errorFallback, retry);
  if (!visible) return deniedFallback;

  return children;
};
